/**
 * Topbar notification bell — durable, per-user.
 *
 * Data source: the ``/notifications`` API. Per-user state (read /
 * dismissed) lives on the server, so the badge survives reloads and
 * is consistent across browser tabs / users.
 *
 * Live refresh: subscribes to the SSE ``notification_created`` event.
 * Whenever one fires, we invalidate the two notifications queries
 * (list + count); React Query refetches and the badge updates.
 *
 * Interactions:
 *   - Opening the dropdown fires ``mark-all-read`` so the badge zeros
 *     immediately (matches the user's mental model of "I've seen them")
 *   - "Dismiss" hides a row from future views
 *   - Click on a row → navigate to the related workflow editor when
 *     a ``related_workflow_id`` is set
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Bell, CheckCircle2, ExternalLink, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { notificationsApi, type NotificationItem } from "@/lib/api-notifications";
import { useLiveEvents } from "@/lib/eventStream";
import { getSupportVisit } from "@/lib/supportSession";
import { cn } from "@/lib/cn";

export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { events, generation, state: liveState } = useLiveEvents();

  // Badge count — single-int query, cheap to refresh frequently.
  const countQ = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: notificationsApi.count,
    staleTime: 5_000,
  });

  // Dropdown list — fetched lazily (only when the menu opens, kept warm
  // afterwards so re-opens are instant).
  const [open, setOpen] = React.useState(false);
  const listQ = useQuery({
    queryKey: ["notifications", "list", "all"],
    queryFn: () => notificationsApi.list({ state: "all", limit: 20 }),
    enabled: open,
    staleTime: 5_000,
  });

  // Live invalidation: any time a new ``notification_created`` event
  // arrives we re-fetch both queries so the badge + open dropdown
  // reflect reality in real time.
  React.useEffect(() => {
    const latest = events[0];
    if (latest && latest.kind === "notification_created") {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation]);

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const dismiss = useMutation({
    mutationFn: notificationsApi.dismiss,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  // A support visitor reads the customer's bell but owns none of it: the backend
  // windows their view to 24h and refuses to store their read-state (ADR-0081 D6),
  // so say the first out loud and stop offering the second.
  const isVisit = getSupportVisit() !== null;

  function onOpenChange(next: boolean) {
    setOpen(next);
    // Catch-up read-marker on open so the badge clears immediately
    // even before the user clicks individual rows. Never for a VISITOR: the backend
    // refuses to store their read-state, so this would be a guaranteed 403 on every
    // open — and if it ever succeeded it would clear the CUSTOMER's badge.
    if (next && !isVisit && (countQ.data?.unread ?? 0) > 0) {
      markAllRead.mutate();
    }
  }

  const unread = countQ.data?.unread ?? 0;
  const items = (listQ.data?.items ?? []).filter((n) => n.dismissed_at === null);

  // Live-stream health folded INTO the bell (was a standalone dot in the topbar
  // that floated untethered + read as cryptic). A HEALTHY stream is silent — we
  // only surface a status pip when it degrades, so the operator sees the signal
  // only when it matters: amber (pulsing) = connecting, muted = offline.
  const liveWarn = liveState !== "open";
  const liveTitle =
    liveState === "connecting"
      ? t("topbar.live.connecting", "Connessione al flusso in tempo reale…")
      : liveState === "closed"
        ? t("topbar.live.offline", "Flusso in tempo reale offline")
        : t("notifications.title", "Notifiche");

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("notifications.title", "Notifications")}
          title={liveTitle}
          className="relative"
        >
          <Bell className="h-4 w-4" strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 font-mono text-[10px] font-medium text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
          {/* Live-stream status pip — bottom-left corner so it never collides
              with the unread badge (top-right). Only rendered when degraded. */}
          {liveWarn && (
            <span
              className={cn(
                "absolute bottom-0.5 left-0.5 h-2 w-2 rounded-full ring-2 ring-surface",
                liveState === "connecting" ? "animate-pulse bg-warning" : "bg-muted-foreground/50",
              )}
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-medium">{t("notifications.title", "Notifications")}</span>
          <Link
            to="/activity"
            className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
            onClick={() => setOpen(false)}
          >
            {t("notifications.viewAll", "View activity")}
            <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
          </Link>
        </div>

        {isVisit && (
          <p className="border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
            {t("notifications.supportWindow", "Sessione di assistenza: solo le ultime 24 ore.")}
          </p>
        )}

        {listQ.isLoading && items.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            {t("notifications.empty", "Nothing to flag right now.")}
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                item={n}
                onDismiss={isVisit ? undefined : () => dismiss.mutate(n.id)}
                onClick={() => setOpen(false)}
              />
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  item,
  onDismiss,
  onClick,
}: {
  item: NotificationItem;
  /** Omitted for a support visitor: the backend refuses to store THEIR read-state
   *  (ADR-0081 D6), so offering the X would only produce a 403 on click. */
  onDismiss?: () => void;
  onClick: () => void;
}) {
  const isUnread = item.read_at === null;
  const Icon = item.severity === "error" ? AlertCircle : CheckCircle2;
  const to = item.related_workflow_id ? `/workflows/${item.related_workflow_id}` : "/activity";

  return (
    <li className={cn("border-b border-border last:border-b-0", isUnread && "bg-accent/5")}>
      <div className="group relative flex items-start gap-2 px-3 py-2 hover:bg-muted/70">
        <Link to={to} onClick={onClick} className="flex flex-1 min-w-0 items-start gap-2">
          <Icon
            className={cn(
              "mt-0.5 h-3.5 w-3.5 shrink-0",
              item.severity === "error" ? "text-danger" : "text-success",
            )}
            strokeWidth={1.5}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="truncate text-xs font-medium">{item.title}</div>
              {/* Burst count chip — only when the same fingerprint has
                  fired more than once within the coalesce window. The
                  red-tinted background draws the eye for high counts
                  without being shouty for "× 2". */}
              {item.occurrence_count > 1 && (
                <span
                  className={cn(
                    "shrink-0 rounded px-1 font-mono text-[10px] tabular-nums",
                    item.occurrence_count >= 100
                      ? "bg-danger/20 text-danger"
                      : "bg-muted text-muted-foreground",
                  )}
                  title={
                    item.last_occurred_at
                      ? `Last occurred ${formatRelative(item.last_occurred_at)}`
                      : undefined
                  }
                >
                  × {item.occurrence_count}
                </span>
              )}
            </div>
            {item.subtitle && (
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                {item.subtitle}
              </div>
            )}
            <div className="mt-0.5 text-[10px] text-muted-foreground">
              {formatRelative(item.created_at)}
            </div>
          </div>
          {isUnread && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
        </Link>
        {onDismiss && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
            aria-label="Dismiss"
            title="Dismiss"
            className="invisible mt-0.5 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground group-hover:visible"
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </li>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleString();
}
